/**
 * WYSIWYG Product Embed Setup
 * Wires the "insert product card" toolbar button into a Quill editor
 * instance. Reuses the same product search input/modal as the single
 * "Target Product" linker (post-product-linker) — see
 * wysiwyg-product-embed-handler.ts for how the two features stay mutually
 * exclusive on that shared input.
 */

import { WysiwygForm } from "../wysiwyg-form";
import { WysiwygProductEmbedRender } from "./wysiwyg-product-embed-render";
import { WysiwygProductEmbedHandler } from "./wysiwyg-product-embed-handler";
import { WysiwygProductEmbedListener } from "./wysiwyg-product-embed-listener";

interface QuillSelectionRange {
    index: number;
    length: number;
}

interface QuillLike {
    getSelection(focus?: boolean): QuillSelectionRange | null;
    getLength(): number;
    setSelection(index: number, length: number, source?: string): void;
    getModule(name: string): { container?: Element | null } | null | undefined;
    insertEmbed(index: number, name: string, value: unknown, source?: string): void;
}

class WysiwygProductEmbedSetup {
    public static init(quill: QuillLike | null, editorDiv: HTMLElement | null, textarea: HTMLTextAreaElement | null): void {
        if (!quill || !(editorDiv instanceof HTMLElement) || !(textarea instanceof HTMLTextAreaElement)) {
            return;
        }

        if (editorDiv.dataset.wysiwygProductEmbedInitialized === "1") {
            return;
        }

        const searchInput = WysiwygForm.getFormProductEmbedSearchInput(textarea);
        if (!(searchInput instanceof HTMLInputElement)) {
            return;
        }

        const toolbarContainer = quill.getModule("toolbar")?.container;
        if (!(toolbarContainer instanceof HTMLElement)) {
            return;
        }

        editorDiv.dataset.wysiwygProductEmbedInitialized = "1";

        const toolbarButton = WysiwygProductEmbedRender.createToolbarButton(toolbarContainer);
        const handler = new WysiwygProductEmbedHandler(quill, searchInput);
        WysiwygProductEmbedListener.bind(toolbarButton, searchInput, handler);
    }
}

export { WysiwygProductEmbedSetup };
